import { Helmet } from 'react-helmet-async'

interface SEOProps {
  title?: string
  description?: string
  canonical?: string
  type?: 'website' | 'article' | 'product'
  image?: string
  imageAlt?: string
  noindex?: boolean
  article?: {
    publishedTime?: string
    modifiedTime?: string
    author?: string
  }
  structuredData?: object | object[]
}

const SITE_NAME = 'JumpstoneHR'
const DEFAULT_TITLE = 'JumpstoneHR | AI-Powered Recruitment Platform'
const DEFAULT_DESCRIPTION = 'JumpstoneHR is an AI-powered hiring decision platform that screens candidates in seconds, not days. Make evidence-based hiring decisions with confidence.'
const DEFAULT_IMAGE = 'https://jumpstonehr.com/images/og-image.png'
const SITE_URL = 'https://jumpstonehr.com'
const TWITTER_HANDLE = '@jumpstonehr'

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  type = 'website',
  image = DEFAULT_IMAGE,
  imageAlt = 'JumpstoneHR - AI-Powered Hiring Platform',
  noindex = false,
  article,
  structuredData,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE
  const canonicalUrl = canonical ? `${SITE_URL}${canonical}` : undefined

  // Default organization structured data
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Jumpstone Technology Inc.',
    alternateName: 'JumpstoneHR',
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo.png`,
    description: 'AI-powered hiring decision platform that helps organizations make evidence-based hiring decisions.',
    foundingDate: '2024',
    founders: [
      {
        '@type': 'Organization',
        name: 'Nanthi Ventures',
        url: 'https://www.nanthiventures.com',
      },
    ],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'CA',
      addressRegion: 'Ontario',
    },
    sameAs: [
      'https://www.linkedin.com/company/jumpstonehr',
      'https://twitter.com/jumpstonehr',
    ],
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'sales',
        email: 'sales@jumpstonehr.com',
      },
      {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: 'support@jumpstonehr.com',
      },
    ],
  }

  // Default website structured data
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description: DEFAULT_DESCRIPTION,
    publisher: {
      '@type': 'Organization',
      name: 'Jumpstone Technology Inc.',
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }

  // Collect all structured data
  const allStructuredData = [
    organizationSchema,
    websiteSchema,
    ...(Array.isArray(structuredData) ? structuredData : structuredData ? [structuredData] : []),
  ]

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific OG tags */}
      {type === 'article' && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {type === 'article' && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {type === 'article' && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:creator" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={imageAlt} />
      
      {/* Additional SEO tags */}
      <meta name="author" content="Jumpstone Technology Inc." />
      <meta name="publisher" content="Jumpstone Technology Inc." />
      <meta name="copyright" content="© 2024-2026 Jumpstone Technology Inc. All rights reserved." />
      
      {/* Language and region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="CA-ON" />
      <meta name="geo.placename" content="Toronto" />
      
      {/* Theme and app */}
      <meta name="theme-color" content="#10b981" />
      <meta name="msapplication-TileColor" content="#10b981" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content={SITE_NAME} />
      
      {/* Verification tags (add your own) */}
      {/* <meta name="google-site-verification" content="your-verification-code" /> */}
      {/* <meta name="msvalidate.01" content="your-bing-verification-code" /> */}
      
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(allStructuredData)}
      </script>
    </Helmet>
  )
}

// Pre-built structured data generators for different page types
export const StructuredData = {
  // Software Application schema for the product
  softwareApplication: () => ({
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'JumpstoneHR',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web',
    description: 'AI-powered hiring decision platform that helps organizations screen candidates and make evidence-based hiring decisions.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'CAD',
      description: 'Free tier available',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      reviewCount: '50',
    },
    featureList: [
      'AI-powered candidate screening',
      'Resume and portfolio analysis',
      'Structured scorecards',
      'Decision recommendations',
      'ATS integration',
    ],
  }),

  // FAQ schema builder
  faq: (questions: { question: string; answer: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map((q) => ({
      '@type': 'Question',
      name: q.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: q.answer,
      },
    })),
  }),

  // Breadcrumb schema builder
  breadcrumb: (items: { name: string; url: string }[]) => ({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://jumpstonehr.com${item.url}`,
    })),
  }),

  // Service schema for pricing tiers
  service: (name: string, description: string, price?: string) => ({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    provider: {
      '@type': 'Organization',
      name: 'Jumpstone Technology Inc.',
    },
    ...(price && {
      offers: {
        '@type': 'Offer',
        price,
        priceCurrency: 'CAD',
      },
    }),
  }),

  // Job posting schema (for careers page)
  jobPosting: (job: {
    title: string
    description: string
    location?: string
    remote?: boolean
    salary?: { min: number; max: number; currency: string }
  }) => ({
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    description: job.description,
    datePosted: new Date().toISOString().split('T')[0],
    hiringOrganization: {
      '@type': 'Organization',
      name: 'Jumpstone Technology Inc.',
      sameAs: 'https://jumpstonehr.com',
    },
    jobLocation: job.remote
      ? { '@type': 'Place', address: { '@type': 'PostalAddress', addressCountry: 'CA' } }
      : undefined,
    jobLocationType: job.remote ? 'TELECOMMUTE' : undefined,
    ...(job.salary && {
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: job.salary.currency,
        value: {
          '@type': 'QuantitativeValue',
          minValue: job.salary.min,
          maxValue: job.salary.max,
          unitText: 'YEAR',
        },
      },
    }),
  }),

  // Local business for contact page
  localBusiness: () => ({
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Jumpstone Technology Inc.',
    image: 'https://jumpstonehr.com/images/logo.png',
    url: 'https://jumpstonehr.com',
    email: 'hello@jumpstonehr.com',
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'Canada',
      addressRegion: 'Ontario',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 43.6532,
      longitude: -79.3832,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    priceRange: '$$',
  }),

  // WebPage schema for generic pages
  webPage: (name: string, description: string, url: string) => ({
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description,
    url: `https://jumpstonehr.com${url}`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'JumpstoneHR',
      url: 'https://jumpstonehr.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Jumpstone Technology Inc.',
    },
  }),
}

export default SEO

